import { CircularProgress, Grid2, Typography, Box } from '@mui/material';
import { withStyles } from '@mui/styles';
import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import useLocalStorageState from '../../utils/useLocalStorageState';
import useProcessedTimeseetsFilters from '../hooks/ProcessedTimesheets/useFilters';
import useFilteredDivisionsTabs from '../hooks/ProcessedTimesheets/useFilteredDivisionsTabs';
import useGetDivisions from '../hooks/useGetDivisions';
import useGetProcessedTimesheets from '../hooks/useGetProcessedTimesheets';
import { COLORS } from '../../utils/Application_Constants';
import PageContainer from '../Components/PageContainer';
import ListViewTable from '../../Shared/Components/ListViewTable/ListViewTable';
import ListViewTableHeaderRow from '../../Shared/Components/ListViewTable/ListViewTableHeaderRow';
import ListViewTableBodyRow from '../../Shared/Components/ListViewTable/ListViewTableBodyRow';
import ListViewTableBodyCell from '../../Shared/Components/ListViewTable/ListViewTableBodyCell';
import FilterDrawer from '../../Shared/Components/FilterDrawer/FilterDrawer';
import Tabs from '../../Shared/Components/Tab/Tabs';
import Tab from '../../Shared/Components/Tab/Tab';
import TabPanel from '../../Shared/Components/Tab/TabPanel';
import { formatUtcDate } from '../../utils/formatDate';
import SearchInputWithDropdown from '../../Shared/Components/SearchInputWithDropdown';
import {
    searchOptions,
    returnProcessedTimesheetsColumns,
} from '../Configurations/processedTimesheetsConfig';
import BodyCell from '../Components/ProcessedTimesheets/Table/BodyCell';
import useThrottle from '../../utils/useThrottle';
import { formatFiltersForApi } from '../../utils/filterUtils';

const styles = () => ({
    header: {
        fontWeight: '700',
        margin: 0,
        marginBottom: '20px',
    },
    eyebrowText: {
        color: COLORS.LT_MIDNIGHT,
        fontSize: '12px',
        marginBottom: '10px',
    },
    description: {
        color: COLORS.LT_MIDNIGHT,
    },
    contentContainer: {
        marginTop: '20px',
    },
    filtersWrapper: {
        paddingTop: '0.5em',
    },
    listViewWrapper: {
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '30px 10px',
        maxHeight: '70vh',
        overflowY: 'auto',
        overflowX: 'auto',
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE and Edge
        '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari, Opera
        },
    },
    resultsDescription: {
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '24px',
        marginTop: '16px',
        marginBottom: '20px',
        color: COLORS.LT_MIDNIGHT,
    },
});

function ProcessedTimesheetsLayout({ classes, title, pageType = 'processedTimesheets' }) {
    const listViewWrapperRef = useRef(null);
    const scrollbarRef = useRef(null);
    const [scrollWidth, setScrollWidth] = useState('100%');
    const { state: sortBy, setValue: setSortBy } = useLocalStorageState(`${pageType}SortField`);
    const { state: sortDirection, setValue: setSortDirection } = useLocalStorageState(
        `${pageType}SortDirection`,
        'asc'
    );
    const { data: divisions, isLoading: isLoadingDivisions } = useGetDivisions();
    const { state: searchData, setValue: setSearch } = useLocalStorageState(`${pageType}Search`, {
        searchWord: '',
        searchColumns: '',
    });
    const { state: tabValue, setValue: setTabValue } = useLocalStorageState(
        `${pageType}SelectedTab`,
        false
    );
    const { state: scrollPosition, setValue: setScrollPosition } = useLocalStorageState(
        `${pageType}ScrollPosition`
    );
    const resetStoredScrollPosition = () => setScrollPosition(0);
    const scrollToTop = () => {
        if (listViewWrapperRef.current) listViewWrapperRef.current.scrollTo({ top: 0 });
    };
    const {
        filters,
        handleApplyFilter,
        filtersSetup,
        isLoadingFilterConfig,
        appliedFiltersDescription,
    } = useProcessedTimeseetsFilters(pageType);
    const { selectedTabValue, selectedTabKey, filteredDivisions } = useFilteredDivisionsTabs(
        tabValue,
        setTabValue,
        isLoadingDivisions,
        divisions,
        filters?.divisions
    );

    const requestParams = useMemo(
        () => ({
            pageSize: 20,
            division: selectedTabKey,
            ...formatFiltersForApi(filters, filtersSetup),
            ...(sortBy ? { orderBy: sortBy, dir: sortDirection } : {}),
            ...(searchData?.searchWord ? searchData : {}),
        }),
        [selectedTabKey, sortBy, sortDirection, filters, filtersSetup, searchData]
    );

    const {
        data: timesheetsPages,
        isLoading,
        isFetching,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useGetProcessedTimesheets(requestParams, { enabled: !!selectedTabValue });

    const groupedTimesheets = useMemo(() => {
        if (!timesheetsPages?.pages) return [];
        const tsGroupedByPayrollDate = timesheetsPages?.pages?.reduce((acc, page) => {
            page.data?.forEach((payroll) => {
                const formattedPayrollDate = formatUtcDate(payroll.payrollDate);
                if (acc[formattedPayrollDate] && acc[formattedPayrollDate].processedTimesheets) {
                    acc[formattedPayrollDate].processedTimesheets = acc[
                        formattedPayrollDate
                    ].processedTimesheets.concat(payroll?.processedTimesheets || []);
                } else {
                    acc[formattedPayrollDate] = {
                        ...payroll,
                        payrollDate: formattedPayrollDate,
                    };
                }
            });
            return acc;
        }, {});
        return Object.values(tsGroupedByPayrollDate);
    }, [timesheetsPages]);

    const throttledFetchNextPage = useThrottle(fetchNextPage, 1000);

    const handleScroll = useCallback(() => {
        if (isFetchingNextPage || !hasNextPage) return;
        const { scrollTop, scrollHeight, clientHeight } = listViewWrapperRef.current;
        setScrollPosition(scrollTop);
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
        if (isAtBottom) throttledFetchNextPage();
    }, [throttledFetchNextPage, isFetchingNextPage, hasNextPage]);

    const throttledScrollHandler = useThrottle(handleScroll, 200);

    useEffect(() => {
        if (isLoading) return; // add scroll listener only after first load
        listViewWrapperRef.current?.addEventListener('scroll', throttledScrollHandler);
        return () =>
            listViewWrapperRef.current?.removeEventListener('scroll', throttledScrollHandler);
    }, [throttledScrollHandler, isLoading]);

    useEffect(() => {
        // reset scroll position when loading (first load or filters changed)
        if (isLoading) {
            resetStoredScrollPosition();
        } else if (!isLoading && scrollPosition && listViewWrapperRef.current) {
            listViewWrapperRef.current.scrollTo({ top: scrollPosition });
        }
    }, [isLoading]);

    // Sync scrollbars and handle resize
    useEffect(() => {
        const tableContent = listViewWrapperRef.current;
        const scrollbar = scrollbarRef.current;

        if (!tableContent || !scrollbar) return;

        const handleTableScroll = () => {
            scrollbar.scrollLeft = tableContent.scrollLeft;
        };

        const handleScrollbarScroll = () => {
            tableContent.scrollLeft = scrollbar.scrollLeft;
        };

        const handleResize = () => {
            if (tableContent) {
                setScrollWidth(`${tableContent.scrollWidth}px`);
            }
        };

        tableContent.addEventListener('scroll', handleTableScroll);
        scrollbar.addEventListener('scroll', handleScrollbarScroll);
        window.addEventListener('resize', handleResize);

        // Update scroll width when content changes
        if (listViewWrapperRef.current) {
            setScrollWidth(`${listViewWrapperRef.current.scrollWidth}px`);
        }

        return () => {
            tableContent.removeEventListener('scroll', handleTableScroll);
            scrollbar.removeEventListener('scroll', handleScrollbarScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [groupedTimesheets]);

    const handleTabChange = (event, value) => {
        setTabValue(value);
        scrollToTop();
    };

    const handleSortChange = useCallback((key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
        scrollToTop();
    }, []);

    const columns = useMemo(() => returnProcessedTimesheetsColumns(pageType), [pageType]);

    return (
        <PageContainer>
            <Grid2 container direction="column" spacing={6}>
                <Grid2 size={12}>
                    <Typography variant="subtitle2" component="p" className={classes.eyebrowText}>
                        Processed Timesheets
                    </Typography>
                    <Typography variant="h3" component="h3" className={classes.header}>
                        {title}
                    </Typography>
                    <Grid2 size={4}>
                        <Typography variant="p" component="p" className={classes.description}>
                            To prioritize your work, filters have been pre-applied. We recommend
                            selecting a division first and filtering by statuses.
                        </Typography>
                    </Grid2>
                </Grid2>
            </Grid2>
            <div className={classes.contentContainer}>
                <Grid2
                    container
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.filtersWrapper}
                >
                    <FilterDrawer
                        isLoading={isLoadingFilterConfig || isLoading}
                        config={filtersSetup}
                        onChange={handleApplyFilter}
                        storedValues={filters}
                    />
                    <SearchInputWithDropdown
                        isLoading={isLoading}
                        options={searchOptions}
                        initialSearch={searchData?.searchWord}
                        initialSearchKey={searchData?.searchColumns}
                        onSubmit={({ search, searchKey }) =>
                            setSearch({ searchWord: search, searchColumns: searchKey })
                        }
                    />
                </Grid2>

                <Typography className={classes.resultsDescription}>
                    # items
                    {appliedFiltersDescription}
                </Typography>

                <Tabs value={selectedTabValue} onChange={handleTabChange}>
                    {!isLoadingDivisions && filteredDivisions?.length ? (
                        filteredDivisions.map((division, index) => (
                            <Tab
                                key={division.name}
                                label={`${division.description} (${division.count || '#'})`}
                                index={index}
                                value={division.description}
                                disabled={isLoading}
                            />
                        ))
                    ) : (
                        <Tab label={<CircularProgress size={15} />} />
                    )}
                </Tabs>
                <TabPanel>
                    {!isLoading && groupedTimesheets.length > 0 ? (
                        <Box sx={{ position: 'relative' }}>
                            <div
                                className={classes.listViewWrapper}
                                ref={listViewWrapperRef}
                                data-testid="list-view-wrapper"
                            >
                                {groupedTimesheets.map((payroll) => (
                                    <ListViewTable
                                        key={payroll.payrollDate}
                                        title={`Payroll Date ${payroll.payrollDate} - (${payroll.processedTimesheets.length})`}
                                    >
                                        <ListViewTableHeaderRow
                                            columns={columns}
                                            onSortChange={handleSortChange}
                                            sortBy={sortBy}
                                            sortDirection={sortDirection}
                                            shouldCleanSort
                                        />
                                        {payroll.processedTimesheets != null &&
                                            payroll.processedTimesheets.length > 0 && (
                                                <>
                                                    {payroll.processedTimesheets.map(
                                                        (timesheet, index) => (
                                                            <ListViewTableBodyRow
                                                                key={`payrollDate:${payroll.payrollDate}|ts:${timesheet.id}`}
                                                                index={index}
                                                            >
                                                                {columns.map((column) => (
                                                                    <ListViewTableBodyCell
                                                                        key={`ts:${timesheet.id}-${column.key}`}
                                                                        size={column.size}
                                                                        sx={column.sx}
                                                                    >
                                                                        <BodyCell
                                                                            timesheet={timesheet}
                                                                            column={column}
                                                                        />
                                                                    </ListViewTableBodyCell>
                                                                ))}
                                                            </ListViewTableBodyRow>
                                                        )
                                                    )}
                                                </>
                                            )}
                                    </ListViewTable>
                                ))}
                                {!hasNextPage && (
                                    <Typography>You&apos;re all caught up!</Typography>
                                )}
                                {isFetchingNextPage && (
                                    <CircularProgress size={50} sx={{ my: 5 }} />
                                )}
                            </div>

                            {/* Static horizontal scrollbar at the bottom */}
                            <Box
                                ref={scrollbarRef}
                                sx={{
                                    width: '100%',
                                    overflowX: 'auto',
                                    overflowY: 'hidden',
                                    position: 'sticky',
                                    bottom: 0,
                                    left: 0,
                                    zIndex: 10,
                                    backgroundColor: 'white',
                                    borderTop: '1px solid #e0e0e0',
                                    paddingTop: '2px',
                                    paddingBottom: '2px',
                                    // Force scrollbar to always be visible
                                    '&::-webkit-scrollbar': {
                                        height: '10px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#888',
                                        borderRadius: '6px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        backgroundColor: '#555',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '1px',
                                        width: scrollWidth,
                                    }}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body1" component="p" p={2}>
                            {isLoading || isFetching ? <CircularProgress /> : 'No results found'}
                        </Typography>
                    )}
                </TabPanel>
            </div>
        </PageContainer>
    );
}

export default withStyles(styles)(ProcessedTimesheetsLayout);
